/**
 * Client for the processing-jobs queue (docs/PROCESSING_JOBS.md): REST calls,
 * polling with backoff, mirroring of server jobs into the task store, and
 * expansion of the compact gridding result.
 */
import api from './api';
import { startTask } from './task-store';

export const jobsApi = {
  submit: (body) => api.post('/api/jobs', body),
  list: (params) => api.get('/api/jobs', { params }),
  get: (id) => api.get(`/api/jobs/${id}`),
  cancel: (id) => api.post(`/api/jobs/${id}/cancel`),
  result: (id) => api.get(`/api/jobs/${id}/result`),
  health: () => api.get('/api/jobs/health'),
};

const TERMINAL = ['succeeded', 'failed', 'cancelled'];
export const isTerminal = (status) => TERMINAL.includes(status);

/** Fetch a job's result: inline, or from the signed (cross-origin, unauthenticated) Storage URL. */
export async function fetchJobResult(jobId) {
  const res = await jobsApi.result(jobId);
  const body = res.data || {};
  if (body.url) {
    const r = await fetch(body.url);
    if (!r.ok) throw new Error(`Could not download the job result (${r.status})`);
    return r.json();
  }
  return body.result ?? null;
}

const round = (x, n) => Number(x.toFixed(n));
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const linspace = (start, stop, n) => {
  if (n <= 1) return [start];
  const out = [];
  for (let k = 0; k < n - 1; k++) out.push(start + (k * (stop - start)) / (n - 1));
  out.push(stop);
  return out;
};

/** Expand a "grid-v1" compact grid into the GeoJSON the sync gridding endpoint returns. */
export function gridResultToGeoJSON(compact) {
  const { method, bbox, grid, min, max } = compact;
  const [nx, ny] = compact.grid_size;
  const gx = linspace(bbox[0], bbox[2], nx);
  const gy = linspace(bbox[1], bbox[3], ny);
  const features = [];
  for (let i = 0; i < ny; i++) {
    for (let j = 0; j < nx; j++) {
      const v = grid[i]?.[j];
      if (!isNum(v)) continue;
      features.push({
        type: 'Feature',
        properties: {
          value: round(v, 4),
          normalized: round(max !== min ? (v - min) / (max - min) : 0.5, 4),
        },
        geometry: { type: 'Point', coordinates: [round(gx[j], 6), round(gy[i], 6)] },
      });
    }
  }
  return {
    type: 'FeatureCollection',
    features,
    metadata: {
      min: round(min, 4),
      max: round(max, 4),
      grid_size: [nx, ny],
      method,
      grid: grid.map((row) => row.map((v) => (isNum(v) ? round(v, 4) : null))),
      ...(compact.preprocess ? { preprocess: compact.preprocess } : {}),
      bbox,
    },
  };
}

/** Map a server job onto task-store fields. */
export function jobToTaskPatch(job) {
  switch (job.status) {
    case 'queued': return { status: 'running', progress: 0, message: 'Queued' };
    case 'running': return { status: 'running', progress: job.progress || 0, message: job.message || '' };
    case 'succeeded': return { status: 'done', progress: 1, message: job.message || 'done' };
    case 'failed': return { status: 'failed', progress: job.progress || 0, message: job.error || 'failed' };
    case 'cancelled': return { status: 'cancelled', progress: job.progress || 0, message: 'cancelled' };
    default: return { status: 'running', progress: 0, message: '' };
  }
}

function abortError() {
  const e = new Error('Aborted');
  e.name = 'AbortError';
  return e;
}

/**
 * Poll a job until it is terminal. Delay starts at minDelay, grows by `factor`
 * up to maxDelay, and resets to minDelay whenever progress or status changes.
 * Transient errors keep polling (with backoff); a 404 rejects.
 * `getJob(id)` may return the job or an axios response wrapping it.
 */
export function pollJob(jobId, {
  onUpdate, signal, minDelay = 2000, maxDelay = 10000, factor = 1.5, getJob = jobsApi.get, initial = null,
} = {}) {
  return new Promise((resolve, reject) => {
    let timer = null;
    let delay = minDelay;
    let lastKey = initial ? `${initial.status}|${initial.progress}` : undefined;

    const onAbort = () => { cleanup(); reject(abortError()); };
    function cleanup() {
      if (timer) clearTimeout(timer);
      timer = null;
      if (signal) signal.removeEventListener('abort', onAbort);
    }
    if (signal) {
      if (signal.aborted) { reject(abortError()); return; }
      signal.addEventListener('abort', onAbort);
    }

    const backoff = () => { delay = Math.min(maxDelay, delay * factor); };
    const schedule = () => { timer = setTimeout(tick, delay); };

    async function tick() {
      timer = null;
      let job;
      try {
        const res = await getJob(jobId);
        job = res && res.id === undefined && res.data && typeof res.data === 'object' ? res.data : res;
      } catch (err) {
        if (signal?.aborted) return;
        if (err?.response?.status === 404) { cleanup(); reject(err); return; }
        backoff();
        schedule();
        return;
      }
      if (signal?.aborted) return;
      if (onUpdate) onUpdate(job);
      if (isTerminal(job.status)) { cleanup(); resolve(job); return; }
      const key = `${job.status}|${job.progress}`;
      if (lastKey !== undefined && key !== lastKey) delay = minDelay;
      else backoff();
      lastKey = key;
      schedule();
    }

    schedule();
  });
}

/**
 * Show a server job in TaskConsole and follow it to its end. Resolves with the
 * terminal job (also for failed/cancelled); rejects only on polling errors.
 */
export async function trackJob(job, { label, onDone, getJob } = {}) {
  const handle = startTask(label || `Job ${job.kind || ''}`.trim(), {
    cancel: () => { jobsApi.cancel(job.id).catch(() => {}); },
  });
  const mirror = (j) => {
    const p = jobToTaskPatch(j);
    if (p.status === 'running') handle.update(p.progress, p.message);
    else if (p.status === 'done') handle.done(p.message);
    else if (p.status === 'failed') handle.fail(p.message);
    else handle.cancelled();
  };
  mirror(job);
  let final = job;
  if (!isTerminal(job.status)) {
    try {
      // A foreground Run button waits on this: poll quickly and never back off past 3 s (V.50).
      final = await pollJob(job.id, {
        onUpdate: mirror, initial: job, minDelay: 1000, maxDelay: 3000, ...(getJob ? { getJob } : {}),
      });
    } catch (err) {
      handle.fail(err?.response?.status === 404 ? 'job not found' : (err?.message || 'failed'));
      throw err;
    }
  }
  if (onDone) onDone(final);
  return final;
}
